// ============================================
// JourneyTimeline
// Journey Timeline visualization — shows the 9 touchpoints
// as a vertical timeline with expand/collapse detail panels.
// Wired to AI Twin engine for automated touchpoint execution.
// ============================================

import { useState, useCallback } from 'react';
import {
  Route,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronRight,
  Loader2,
  Minus,
} from 'lucide-react';
import { runSingleTwin } from '../../../services/patientJourney/twinService';
import { saveTestRun } from '../../../services/firestore/pjeService';
import { getJourneyConfig } from '../../../config/journeyConfig';
import type {
  TestPersona,
  BehaviorModel,
  Touchpoint as EngineTouchpoint,
  CheckResult,
  CheckItem,
  Message,
} from '../../../types/patientJourney';
import { DEFAULT_CONTEXT } from '../../../types/patientJourney';
import type { TwinConversationResult } from '../../../services/patientJourney/twinEngine';

// ── Types ──

type TouchpointStatus = 'not_run' | 'running' | 'passed' | 'failed' | 'skipped';

interface TimelineTouchpoint {
  id: string;
  order: number;
  label: string;
  type: EngineTouchpoint['type'];
  trigger: string;
  validates: string;
}

interface TouchpointResult {
  status: TouchpointStatus;
  passRate: number;
  checkResults: CheckResult[];
  conversationLog: Message[];
  duration: number;
  error?: string;
}

interface JourneySummary {
  totalRun: number;
  passed: number;
  failed: number;
  overallPassRate: number;
  totalDuration: number;
}

// ── Default Test Persona (Sarah Mitchell — Clean Gate 1) ──

const DEFAULT_PERSONA: TestPersona = {
  personaId: 'journey-default-sarah',
  name: 'Sarah Mitchell',
  archetype: 'Clean Gate 1',
  demographics: { name: 'Sarah Mitchell', age: 47, sex: 'Female', state: 'CA' },
  labs: [
    { biomarker: 'Estradiol', value: 38, unit: 'pg/mL', conventionalRange: '12.5-166', functionalRange: '50-100', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
    { biomarker: 'Progesterone', value: 1.2, unit: 'ng/mL', conventionalRange: '1.8-24', functionalRange: '10-20', status: 'below_conventional', trigger: true, timestamp: new Date().toISOString() },
    { biomarker: 'DHEA-S', value: 95, unit: '\u00b5g/dL', conventionalRange: '35-430', functionalRange: '150-350', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
    { biomarker: 'FSH', value: 28, unit: 'mIU/mL', conventionalRange: '3.5-12.5', functionalRange: '3.5-8.0', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
    { biomarker: 'Cortisol (AM)', value: 22, unit: '\u00b5g/dL', conventionalRange: '6.2-19.4', functionalRange: '10-15', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
  ],
  medications: [],
  conditions: ['Perimenopause', 'Fatigue', 'Sleep disruption'],
  contraindications: [],
  allergens: [],
  contextModifiers: { ...DEFAULT_CONTEXT, motivation: 'concerned', baselineStress: 'moderate' },
  journeyTouchpoints: [],
  validationChecklist: [],
};

const DEFAULT_BEHAVIOR: BehaviorModel = {
  communicationStyle: 'conversational',
  questionFrequency: 'moderate',
  attentionSpan: 'medium',
  decisionSpeed: 'moderate',
  skepticismLevel: 40,
  complianceTendency: 60,
  priceResistance: 30,
  emotionalExpressiveness: 50,
  objectionPatterns: [
    { pattern: 'price', probability: 0.3 },
    { pattern: 'evidence', probability: 0.2 },
  ],
  dataSource: 'manual',
  dataVersion: 1,
};

const MAX_TURNS = 15;

// ── Validation Checklists per Touchpoint ──

const TOUCHPOINT_CHECKS: Record<string, CheckItem[]> = {
  first_contact: [
    { checkId: 'JT-01-A', check: 'Protocol matching based on uploaded labs', category: 'clinical', severity: 'critical', automatable: true },
    { checkId: 'JT-01-B', check: 'Product recommendation appropriate for biomarkers', category: 'clinical', severity: 'critical', automatable: true },
    { checkId: 'JT-01-C', check: 'Nurse shows empathy and acknowledges patient concerns', category: 'conversational', severity: 'major', automatable: true },
  ],
  checkout: [
    { checkId: 'JT-02-A', check: 'Correct pricing presented for recommended tier', category: 'pricing', severity: 'critical', automatable: true },
    { checkId: 'JT-02-B', check: 'Subscription setup explained clearly', category: 'conversational', severity: 'major', automatable: true },
  ],
  mid_protocol: [
    { checkId: 'JT-03-A', check: 'Adherence inquiry performed naturally', category: 'clinical', severity: 'major', automatable: true },
    { checkId: 'JT-03-B', check: 'No premature escalation to Gate 2', category: 'compliance', severity: 'critical', automatable: true },
  ],
  retest: [
    { checkId: 'JT-04-A', check: 'Baseline comparison provided with specific values', category: 'clinical', severity: 'critical', automatable: true },
    { checkId: 'JT-04-B', check: 'Gate 2 trigger logic applied correctly', category: 'compliance', severity: 'critical', automatable: true },
  ],
  med_change: [
    { checkId: 'JT-05-A', check: 'Interaction database consulted for new medication', category: 'clinical', severity: 'critical', automatable: true },
    { checkId: 'JT-05-B', check: 'Stack adjustment recommended based on interactions', category: 'clinical', severity: 'major', automatable: true },
  ],
  gate2_transition: [
    { checkId: 'JT-06-A', check: 'Provider routing based on patient state', category: 'compliance', severity: 'critical', automatable: true },
    { checkId: 'JT-06-B', check: 'Consent obtained before escalation', category: 'compliance', severity: 'critical', automatable: true },
    { checkId: 'JT-06-C', check: 'No Rx recommendations made at Gate 1 level', category: 'compliance', severity: 'critical', automatable: true },
  ],
  protocol_success: [
    { checkId: 'JT-07-A', check: 'Step-down recommendation provided', category: 'clinical', severity: 'major', automatable: true },
    { checkId: 'JT-07-B', check: 'Maintenance plan outlined clearly', category: 'clinical', severity: 'major', automatable: true },
  ],
  cancellation: [
    { checkId: 'JT-08-A', check: 'Refund policy communicated per TOS', category: 'compliance', severity: 'critical', automatable: true },
    { checkId: 'JT-08-B', check: 'Pause option offered before cancellation', category: 'conversational', severity: 'major', automatable: true },
  ],
  re_engagement: [
    { checkId: 'JT-09-A', check: 'Patient history recalled from previous sessions', category: 'clinical', severity: 'critical', automatable: true },
    { checkId: 'JT-09-B', check: 'Updated interaction check performed', category: 'clinical', severity: 'major', automatable: true },
  ],
};

// ── Touchpoint Data (labels/triggers from journey config) ──

const VALIDATES_MAP: Record<string, string> = {
  first_contact: 'Protocol matching, product recommendations, interactions, pricing, disclaimers',
  checkout: 'Correct products, pricing tier, subscription creation, welcome flow',
  mid_protocol: 'Adherence check, symptom inquiry, no premature escalation',
  retest: 'Baseline comparison, success/unchanged/worsened, Gate 2 trigger logic',
  med_change: 'Interaction DB re-fires, stack adjusted, conflicts flagged',
  gate2_transition: 'Provider routing, combined stack, state rules, consent',
  protocol_success: 'Step-down recommendation, maintenance plan, subscription adjustment',
  cancellation: 'Refund policy per TOS, pause option offered, clinical implications',
  re_engagement: 'History recall, comparison to historical labs, updated interaction check',
};

function buildStandardTouchpoints(): TimelineTouchpoint[] {
  const config = getJourneyConfig();
  return config.touchpoints.map((tp, i) => ({
    id: `TP-${String(i + 1).padStart(2, '0')}`,
    order: tp.order,
    label: tp.label,
    type: tp.type,
    trigger: tp.trigger,
    validates: VALIDATES_MAP[tp.type] ?? '',
  }));
}

const STANDARD_TOUCHPOINTS: TimelineTouchpoint[] = buildStandardTouchpoints();

// ── Sample Conversation Scripts ──

const SAMPLE_SCRIPTS: Record<string, { role: 'patient' | 'nurse'; text: string }[]> = {
  first_contact: [
    { role: 'nurse', text: 'Welcome! I see you\'ve uploaded your lab results. Let me take a look at your biomarkers and build a personalized protocol.' },
    { role: 'patient', text: 'Sure, I\'m a bit nervous about what they might show.' },
    { role: 'nurse', text: 'That\'s completely normal. I\'ll walk you through everything step by step. Your ferritin is at 18 ng/mL which is below optimal range...' },
  ],
  checkout: [
    { role: 'nurse', text: 'Based on your labs, here\'s your Gate 1 protocol bundle. Would you like me to walk through each product?' },
    { role: 'patient', text: 'How much will this cost per month?' },
    { role: 'nurse', text: 'Your Essentials tier is $49/month and includes all three supplements in your protocol. Shall I break down the pricing?' },
  ],
  mid_protocol: [
    { role: 'nurse', text: 'You\'re 5 weeks into your protocol! How are you feeling? Have you been consistent with your daily doses?' },
    { role: 'patient', text: 'Pretty good, though I forgot a few days last week.' },
    { role: 'nurse', text: 'That\'s okay, occasional misses happen. Your adherence is tracking at about 85% which is still good. Any new symptoms?' },
  ],
  retest: [
    { role: 'nurse', text: 'Great news -- your 90-day retest results are in! Let me compare them to your baseline.' },
    { role: 'patient', text: 'I\'m anxious to see if things improved.' },
    { role: 'nurse', text: 'Your ferritin moved from 18 to 42 ng/mL -- that\'s a significant improvement into functional range!' },
  ],
  med_change: [
    { role: 'patient', text: 'My doctor just prescribed metformin. Will that interact with my supplements?' },
    { role: 'nurse', text: 'Let me check the interaction database... Metformin can reduce B12 absorption. I\'m flagging this and adjusting your stack to add methylcobalamin.' },
  ],
  gate2_transition: [
    { role: 'nurse', text: 'Your biomarkers show some values that would benefit from clinical intervention. I\'d like to connect you with a telehealth provider.' },
    { role: 'patient', text: 'What does that involve?' },
    { role: 'nurse', text: 'A licensed provider in your state will review your labs and may prescribe targeted treatments alongside your supplement protocol.' },
  ],
  protocol_success: [
    { role: 'nurse', text: 'Congratulations! Your last two retests show all biomarkers in functional range. You\'ve hit protocol success!' },
    { role: 'patient', text: 'That\'s amazing! What happens now?' },
    { role: 'nurse', text: 'I recommend transitioning to a maintenance plan with reduced dosing. Your subscription will adjust accordingly.' },
  ],
  cancellation: [
    { role: 'patient', text: 'I need to cancel my subscription.' },
    { role: 'nurse', text: 'I understand. Before we process that, would you like to consider a pause instead? You can resume anytime without losing your history.' },
    { role: 'patient', text: 'What about a refund?' },
    { role: 'nurse', text: 'Per our terms, you\'re within the 30-day window so you\'re eligible for a full refund. I do want to mention that stopping mid-protocol may affect your progress.' },
  ],
  re_engagement: [
    { role: 'nurse', text: 'Welcome back! It\'s been about 4 months since your last visit. I still have all your history and previous labs on file.' },
    { role: 'patient', text: 'I fell off track but want to get back on it.' },
    { role: 'nurse', text: 'No judgment at all. Let me pull up your last results and check if any interactions need updating with your current medications.' },
  ],
};

// ── Status Config ──

const STATUS_CONFIG: Record<TouchpointStatus, { icon: typeof Clock; color: string; bgColor: string; label: string }> = {
  not_run: { icon: Clock, color: 'text-text-muted', bgColor: 'bg-bg-elevated', label: 'Not Run' },
  running: { icon: Loader2, color: 'text-gold', bgColor: 'bg-gold/10', label: 'Running' },
  passed: { icon: CheckCircle2, color: 'text-emerald-400', bgColor: 'bg-emerald-400/10', label: 'Passed' },
  failed: { icon: XCircle, color: 'text-critical', bgColor: 'bg-critical/10', label: 'Failed' },
  skipped: { icon: Minus, color: 'text-text-muted', bgColor: 'bg-bg-elevated', label: 'Skipped' },
};

// ── Helpers ──

function generateRunId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `jt-run-${ts}-${rand}`;
}

/** Build an EngineTouchpoint from a TimelineTouchpoint for the twin engine */
function buildEngineTouchpoint(tp: TimelineTouchpoint): EngineTouchpoint {
  const checks = TOUCHPOINT_CHECKS[tp.type] || [];
  const script = SAMPLE_SCRIPTS[tp.type] || [];

  return {
    touchpointId: tp.id,
    type: tp.type,
    order: tp.order,
    label: tp.label,
    trigger: tp.trigger,
    conversationScript: script.map((s) => ({
      speaker: s.role === 'patient' ? 'patient' as const : 'expected_nurse' as const,
      text: s.text,
    })),
    validationChecklist: checks,
  };
}

// ── Component ──

export function JourneyTimeline() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Run state
  const [runningTouchpoint, setRunningTouchpoint] = useState<string | null>(null);
  const [isRunningJourney, setIsRunningJourney] = useState(false);
  const [journeyProgress, setJourneyProgress] = useState<{ current: number; total: number } | null>(null);
  const [touchpointResults, setTouchpointResults] = useState<Map<string, TouchpointResult>>(new Map());
  const [journeySummary, setJourneySummary] = useState<JourneySummary | null>(null);
  const [journeyError, setJourneyError] = useState<string | null>(null);

  const isAnyRunning = runningTouchpoint !== null || isRunningJourney;

  // Derived counts
  const passedCount = Array.from(touchpointResults.values()).filter((r) => r.status === 'passed').length;
  const completedCount = Array.from(touchpointResults.values()).filter((r) => r.status === 'passed' || r.status === 'failed').length;

  /** Get the display status for a touchpoint */
  const getStatus = useCallback((tpId: string): TouchpointStatus => {
    if (runningTouchpoint === tpId) return 'running';
    const result = touchpointResults.get(tpId);
    if (result) return result.status;
    return 'not_run';
  }, [runningTouchpoint, touchpointResults]);

  /** Run a single touchpoint */
  const handleRunTouchpoint = useCallback(async (tp: TimelineTouchpoint) => {
    if (isAnyRunning) return;

    setRunningTouchpoint(tp.id);
    setJourneyError(null);

    try {
      const engineTp = buildEngineTouchpoint(tp);
      const result: TwinConversationResult = await runSingleTwin(
        DEFAULT_PERSONA,
        DEFAULT_BEHAVIOR,
        engineTp,
        MAX_TURNS,
      );

      const totalChecks = result.checkResults.length;
      const passedChecks = result.checkResults.filter((c) => c.pass === true).length;
      const didPass = totalChecks > 0 ? (passedChecks / totalChecks) >= 0.7 : result.conversationLog.length > 0;

      const tpResult: TouchpointResult = {
        status: didPass ? 'passed' : 'failed',
        passRate: result.passRate,
        checkResults: result.checkResults,
        conversationLog: result.conversationLog,
        duration: result.duration,
      };

      setTouchpointResults((prev) => {
        const next = new Map(prev);
        next.set(tp.id, tpResult);
        return next;
      });

      // Persist to Firestore
      try {
        await saveTestRun({
          runId: generateRunId(),
          suiteId: 'journey-timeline',
          personaId: DEFAULT_PERSONA.personaId,
          touchpointId: tp.id,
          startedAt: new Date(Date.now() - result.duration).toISOString(),
          completedAt: new Date().toISOString(),
          status: 'completed',
          checkResults: result.checkResults,
          conversationLog: result.conversationLog,
          scorer: 'ai_twin',
          passRate: result.passRate,
          suiteVersion: 1,
        });
      } catch {
        // Firestore save failure is non-blocking
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setTouchpointResults((prev) => {
        const next = new Map(prev);
        next.set(tp.id, {
          status: 'failed',
          passRate: 0,
          checkResults: [],
          conversationLog: [],
          duration: 0,
          error: errorMsg,
        });
        return next;
      });
    } finally {
      setRunningTouchpoint(null);
    }
  }, [isAnyRunning]);

  /** Run full journey — all 9 touchpoints sequentially */
  const handleRunFullJourney = useCallback(async () => {
    if (isAnyRunning) return;

    setIsRunningJourney(true);
    setJourneyError(null);
    setJourneySummary(null);
    setTouchpointResults(new Map());

    const startTime = Date.now();
    let passed = 0;
    let failed = 0;
    let totalPassRate = 0;

    for (let i = 0; i < STANDARD_TOUCHPOINTS.length; i++) {
      const tp = STANDARD_TOUCHPOINTS[i];
      setRunningTouchpoint(tp.id);
      setJourneyProgress({ current: i + 1, total: STANDARD_TOUCHPOINTS.length });

      try {
        const engineTp = buildEngineTouchpoint(tp);
        const result: TwinConversationResult = await runSingleTwin(
          DEFAULT_PERSONA,
          DEFAULT_BEHAVIOR,
          engineTp,
          MAX_TURNS,
        );

        const totalChecks = result.checkResults.length;
        const passedChecks = result.checkResults.filter((c) => c.pass === true).length;
        const didPass = totalChecks > 0 ? (passedChecks / totalChecks) >= 0.7 : result.conversationLog.length > 0;

        if (didPass) passed++;
        else failed++;
        totalPassRate += result.passRate;

        const tpResult: TouchpointResult = {
          status: didPass ? 'passed' : 'failed',
          passRate: result.passRate,
          checkResults: result.checkResults,
          conversationLog: result.conversationLog,
          duration: result.duration,
        };

        setTouchpointResults((prev) => {
          const next = new Map(prev);
          next.set(tp.id, tpResult);
          return next;
        });

        // Persist to Firestore (non-blocking)
        saveTestRun({
          runId: generateRunId(),
          suiteId: 'journey-timeline-full',
          personaId: DEFAULT_PERSONA.personaId,
          touchpointId: tp.id,
          startedAt: new Date(Date.now() - result.duration).toISOString(),
          completedAt: new Date().toISOString(),
          status: 'completed',
          checkResults: result.checkResults,
          conversationLog: result.conversationLog,
          scorer: 'ai_twin',
          passRate: result.passRate,
          suiteVersion: 1,
        }).catch(() => { /* non-blocking */ });

        // Small delay between runs to avoid rate limits
        if (i < STANDARD_TOUCHPOINTS.length - 1) {
          await new Promise((r) => setTimeout(r, 1500));
        }
      } catch (err) {
        failed++;
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        setTouchpointResults((prev) => {
          const next = new Map(prev);
          next.set(tp.id, {
            status: 'failed',
            passRate: 0,
            checkResults: [],
            conversationLog: [],
            duration: 0,
            error: errorMsg,
          });
          return next;
        });
      }
    }

    const totalRun = passed + failed;
    setJourneySummary({
      totalRun,
      passed,
      failed,
      overallPassRate: totalRun > 0 ? Math.round(totalPassRate / totalRun) : 0,
      totalDuration: Date.now() - startTime,
    });

    setRunningTouchpoint(null);
    setIsRunningJourney(false);
    setJourneyProgress(null);
  }, [isAnyRunning]);

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-display text-gold flex items-center gap-2">
            <Route size={20} />
            Journey Timeline
          </h3>
          <p className="text-base text-text-secondary mt-1">
            9 standard touchpoints validating the complete patient journey
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Progress */}
          <span className="text-base text-text-muted">
            {passedCount} of {STANDARD_TOUCHPOINTS.length} touchpoints validated
          </span>
          {/* Run all button */}
          <button
            onClick={handleRunFullJourney}
            disabled={isAnyRunning}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-base transition-colors ${
              isAnyRunning
                ? 'bg-gold/10 text-gold/50 cursor-not-allowed'
                : 'bg-gold/20 text-gold hover:bg-gold/30 cursor-pointer'
            }`}
          >
            {isRunningJourney ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Play size={16} />
            )}
            {isRunningJourney && journeyProgress
              ? `Running ${journeyProgress.current} of ${journeyProgress.total}...`
              : 'Run Full Journey'}
          </button>
        </div>
      </div>

      {/* ── Journey Progress Bar (during full run) ── */}
      {isRunningJourney && journeyProgress && (
        <div className="bg-bg-card border border-bg-tertiary rounded-xl px-5 py-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Loader2 size={14} className="animate-spin text-gold" />
              <span className="text-base text-text-primary font-medium">
                Running touchpoint {journeyProgress.current} of {journeyProgress.total}
              </span>
            </div>
            <span className="text-sm text-text-muted">
              {completedCount} completed
            </span>
          </div>
          <div className="w-full bg-bg-tertiary rounded-full h-2 overflow-hidden">
            <div
              className={`bg-gold h-2 rounded-full transition-all duration-500 ${
                journeyProgress.current === 1 ? 'w-[11%]' :
                journeyProgress.current === 2 ? 'w-[22%]' :
                journeyProgress.current === 3 ? 'w-[33%]' :
                journeyProgress.current === 4 ? 'w-[44%]' :
                journeyProgress.current === 5 ? 'w-[55%]' :
                journeyProgress.current === 6 ? 'w-[66%]' :
                journeyProgress.current === 7 ? 'w-[77%]' :
                journeyProgress.current === 8 ? 'w-[88%]' :
                'w-full'
              }`}
            />
          </div>
        </div>
      )}

      {/* ── Journey Summary (after full run) ── */}
      {journeySummary && !isRunningJourney && (
        <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
            <Route size={16} className="text-gold" />
            <span className="text-base font-semibold font-display text-gold">
              Journey Summary
            </span>
          </div>
          <div className="px-5 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-bg-elevated rounded-lg border border-bg-tertiary px-4 py-3 text-center">
                <p className={`text-2xl font-semibold ${
                  journeySummary.overallPassRate >= 80
                    ? 'text-emerald-400'
                    : journeySummary.overallPassRate >= 50
                    ? 'text-amber-400'
                    : 'text-critical'
                }`}>
                  {journeySummary.overallPassRate}%
                </p>
                <p className="text-xs text-text-muted mt-1">Overall Pass Rate</p>
              </div>
              <div className="bg-bg-elevated rounded-lg border border-bg-tertiary px-4 py-3 text-center">
                <p className="text-2xl font-semibold text-emerald-400">{journeySummary.passed}</p>
                <p className="text-xs text-text-muted mt-1">Passed</p>
              </div>
              <div className="bg-bg-elevated rounded-lg border border-bg-tertiary px-4 py-3 text-center">
                <p className={`text-2xl font-semibold ${journeySummary.failed > 0 ? 'text-critical' : 'text-text-muted'}`}>
                  {journeySummary.failed}
                </p>
                <p className="text-xs text-text-muted mt-1">Failed</p>
              </div>
              <div className="bg-bg-elevated rounded-lg border border-bg-tertiary px-4 py-3 text-center">
                <p className="text-2xl font-semibold text-text-primary">
                  {(journeySummary.totalDuration / 1000).toFixed(1)}s
                </p>
                <p className="text-xs text-text-muted mt-1">Total Duration</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Journey Error ── */}
      {journeyError && (
        <div className="bg-critical/10 border border-critical/20 rounded-xl px-5 py-4 flex items-start gap-2">
          <XCircle size={16} className="text-critical flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-base font-medium text-critical">Journey Error</p>
            <p className="text-base text-critical/80 mt-0.5">{journeyError}</p>
          </div>
        </div>
      )}

      {/* ── Timeline ── */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gold/20" />

        <div className="space-y-3">
          {STANDARD_TOUCHPOINTS.map((tp) => {
            const isExpanded = expandedId === tp.id;
            const status = getStatus(tp.id);
            const statusCfg = STATUS_CONFIG[status];
            const StatusIcon = statusCfg.icon;
            const script = SAMPLE_SCRIPTS[tp.type] || [];
            const result = touchpointResults.get(tp.id);
            const isThisRunning = status === 'running';

            return (
              <div key={tp.id} className="relative pl-14">
                {/* ── Number badge on the timeline ── */}
                <div className="absolute left-0 top-3 w-12 h-12 flex items-center justify-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-bold border-2 z-10 ${
                      status === 'passed'
                        ? 'bg-emerald-400/20 border-emerald-400 text-emerald-400'
                        : status === 'failed'
                        ? 'bg-critical/20 border-critical text-critical'
                        : status === 'running'
                        ? 'bg-gold/20 border-gold text-gold'
                        : status === 'skipped'
                        ? 'bg-bg-card border-bg-tertiary text-text-muted'
                        : 'bg-bg-card border-bg-tertiary text-text-muted'
                    }`}
                  >
                    {isThisRunning ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : status === 'passed' ? (
                      <CheckCircle2 size={18} />
                    ) : status === 'failed' ? (
                      <XCircle size={18} />
                    ) : status === 'skipped' ? (
                      <Minus size={18} />
                    ) : (
                      tp.order
                    )}
                  </div>
                </div>

                {/* ── Touchpoint Card ── */}
                <div
                  className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden transition-colors hover:border-gold/20"
                >
                  {/* Card header -- clickable */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : tp.id)}
                    className="w-full flex items-center gap-3 p-5 hover:bg-bg-elevated/50 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-semibold text-text-primary">
                          {tp.label}
                        </span>
                        <span className="text-sm text-text-muted font-mono">
                          {tp.id}
                        </span>
                        {result && result.passRate > 0 && (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-lg ${
                            result.passRate >= 80
                              ? 'text-emerald-400 bg-emerald-400/10'
                              : result.passRate >= 50
                              ? 'text-amber-400 bg-amber-400/10'
                              : 'text-critical bg-critical/10'
                          }`}>
                            {result.passRate}%
                          </span>
                        )}
                      </div>
                      <p className="text-base text-text-secondary mt-1">
                        {tp.trigger}
                      </p>
                    </div>

                    {/* Status badge */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={`flex items-center gap-1.5 text-sm font-semibold px-2.5 py-1 rounded-lg ${statusCfg.color} ${statusCfg.bgColor}`}
                      >
                        <StatusIcon size={14} className={isThisRunning ? 'animate-spin' : ''} />
                        {statusCfg.label}
                      </span>
                      <ChevronRight
                        size={18}
                        className={`text-text-muted transition-transform ${
                          isExpanded ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="border-t border-bg-tertiary px-5 py-5 space-y-4">
                      {/* What this validates */}
                      <div>
                        <h4 className="text-base font-display text-gold mb-2">
                          Validates
                        </h4>
                        <p className="text-base text-text-secondary">
                          {tp.validates}
                        </p>
                      </div>

                      {/* Validation checks list */}
                      {TOUCHPOINT_CHECKS[tp.type] && (
                        <div>
                          <h4 className="text-base font-display text-gold mb-2">
                            Validation Checks
                          </h4>
                          <div className="space-y-1.5">
                            {TOUCHPOINT_CHECKS[tp.type].map((check) => {
                              const checkResult = result?.checkResults.find((cr) => cr.checkId === check.checkId);
                              return (
                                <div
                                  key={check.checkId}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-base ${
                                    checkResult?.pass === true
                                      ? 'bg-emerald-400/5 border-emerald-400/20'
                                      : checkResult?.pass === false
                                      ? 'bg-critical/5 border-critical/20'
                                      : 'bg-bg-elevated/50 border-bg-tertiary'
                                  }`}
                                >
                                  <div className="flex-shrink-0">
                                    {checkResult?.pass === true ? (
                                      <CheckCircle2 size={14} className="text-emerald-400" />
                                    ) : checkResult?.pass === false ? (
                                      <XCircle size={14} className="text-critical" />
                                    ) : (
                                      <Clock size={14} className="text-text-muted" />
                                    )}
                                  </div>
                                  <span className={`flex-1 text-sm ${
                                    checkResult?.pass === true
                                      ? 'text-emerald-400'
                                      : checkResult?.pass === false
                                      ? 'text-critical'
                                      : 'text-text-secondary'
                                  }`}>
                                    {check.check}
                                  </span>
                                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${
                                    check.severity === 'critical'
                                      ? 'text-critical/70 bg-critical/5'
                                      : check.severity === 'major'
                                      ? 'text-amber-400/70 bg-amber-400/5'
                                      : 'text-text-muted bg-bg-elevated'
                                  }`}>
                                    {check.severity}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Conversation preview (sample) or actual results */}
                      {result && result.conversationLog.length > 0 ? (
                        <div>
                          <h4 className="text-base font-display text-gold mb-3">
                            Conversation Log ({result.conversationLog.length} messages, {(result.duration / 1000).toFixed(1)}s)
                          </h4>
                          <div className="space-y-2 max-h-80 overflow-y-auto">
                            {result.conversationLog.map((msg, i) => {
                              const isNurse = msg.role === 'ai_nurse';
                              return (
                                <div
                                  key={i}
                                  className={`flex ${isNurse ? 'justify-start' : 'justify-end'}`}
                                >
                                  <div
                                    className={`max-w-[80%] rounded-xl px-4 py-3 text-base ${
                                      isNurse
                                        ? 'bg-gold/10 border border-gold/20 text-text-primary'
                                        : 'bg-bg-elevated border border-bg-tertiary text-text-primary'
                                    }`}
                                  >
                                    <div
                                      className={`text-xs font-semibold mb-1 ${
                                        isNurse ? 'text-gold' : 'text-text-muted'
                                      }`}
                                    >
                                      {isNurse ? 'AI Nurse' : 'Patient (Twin)'}
                                    </div>
                                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                      {msg.content}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : script.length > 0 && !result ? (
                        <div>
                          <h4 className="text-base font-display text-gold mb-3">
                            Conversation Preview
                          </h4>
                          <div className="space-y-2">
                            {script.map((turn, i) => (
                              <div
                                key={i}
                                className={`flex ${
                                  turn.role === 'patient'
                                    ? 'justify-end'
                                    : 'justify-start'
                                }`}
                              >
                                <div
                                  className={`max-w-[80%] rounded-xl px-4 py-3 text-base ${
                                    turn.role === 'patient'
                                      ? 'bg-bg-elevated border border-bg-tertiary text-text-primary'
                                      : 'bg-gold/10 border border-gold/20 text-text-primary'
                                  }`}
                                >
                                  <div
                                    className={`text-xs font-semibold mb-1 ${
                                      turn.role === 'patient'
                                        ? 'text-text-muted'
                                        : 'text-gold'
                                    }`}
                                  >
                                    {turn.role === 'patient' ? 'Patient' : 'AI Nurse'}
                                  </div>
                                  {turn.text}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {/* Error display for this touchpoint */}
                      {result?.error && (
                        <div className="bg-critical/10 border border-critical/20 rounded-lg px-4 py-3 flex items-start gap-2">
                          <XCircle size={14} className="text-critical flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-critical/80">{result.error}</p>
                        </div>
                      )}

                      {/* Run button */}
                      <div className="flex items-center justify-end pt-2 border-t border-bg-tertiary">
                        <button
                          onClick={() => handleRunTouchpoint(tp)}
                          disabled={isAnyRunning}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-base transition-colors ${
                            isAnyRunning
                              ? 'bg-gold/10 text-gold/50 cursor-not-allowed'
                              : 'bg-gold/20 text-gold hover:bg-gold/30 cursor-pointer'
                          }`}
                        >
                          {isThisRunning ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Play size={16} />
                          )}
                          {isThisRunning ? 'Running...' : result ? 'Re-run Touchpoint' : 'Run Touchpoint'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
